import React, { useState, useEffect, useCallback } from "react";
import "../page/productList.css";
import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import ProductHeader from "../component/productHeader";
import ProductTable from "../component/productTable";
import ModalAddProduct from "../component/modalAddProduct";
import PaginationFooter from "../../../components/footer/paginationFooter";
import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";
import ModalProductInfo from "../component/modalProductInfo";

const ProductList = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);

  const [isOpenModalAddProduct, setIsOpenModalAddProduct] = useState(false);
  const [selectedProductData, setSelectedProductData] = useState({
    product_id: "",
    product_name: "",
    product_category: {
      product_category_id: "",
      product_category_name: "",
    },
    product_unit: {
      product_unit_id: "",
      product_unit_name: "",
    },
    product_rack: "",
    product_hjb: "",
    product_hjr: "",
    product_partner_selling_price: "",
    product_total_stock: 0,
  });
  const [listProductData, setListProductData] = useState(null);
  const [isOpenModalDeleteProduct, setIsOpenModalDeleteProduct] =
    useState(false);
  const [idProductToDelete, setIdProductToDelete] = useState("");
  const [searchProductKeyword, setSearchProductKeyword] = useState("");
  const [isOpenModalProductInfo, setIsOpenModalProductInfo] = useState(false);
  const [productDataNote, setProductDataNote] = useState({
    product_id: "",
    product_name: "",
    product_stock: 0,
  });

  // =====================
  // Function Definition
  // =====================

  const addNewProduct = () => {
    resetSelectedProductDataValue();
    setIsOpenModalAddProduct(true);
  };

  const onEditProduct = (productData) => {
    setSelectedProductData(productData);
    setIsOpenModalAddProduct(true);
  };

  const onDeleteProduct = (productData) => {
    setIsOpenModalDeleteProduct(true);
    setIdProductToDelete(productData.product_id);
  };

  const onInfoProductClicked = (productData) => {
    const productInfo = {
      product_id: productData.product_id,
      product_name: productData.product_name,
      product_stock: productData.product_total_stock,
    };

    setProductDataNote(productInfo);
    setIsOpenModalProductInfo(true);
  };

  const onDeleteProductConfirmed = () => {
    setIsOpenModalDeleteProduct(false);
    deleteProduct();
  };

  const onCloseModalDeleteProduct = () => {
    setIdProductToDelete("");
    setIsOpenModalDeleteProduct(false);
  };

  const onCloseModal = () => {
    resetSelectedProductDataValue();
    setIsOpenModalAddProduct(false);
  };

  const onCloseModalProductInfo = () => {
    setIsOpenModalProductInfo(false);
    setProductDataNote({
      product_id: "",
      product_name: "",
      product_stock: 0,
    });
    getProductData();
  };

  const onSubmitNewProduct = (productData, isEditData) => {
    setIsOpenModalAddProduct(false);
    if (isEditData) {
      editProduct(productData);
    } else {
      createNewProduct(productData);
    }
  };

  const resetSelectedProductDataValue = () => {
    setSelectedProductData({
      product_id: "",
      product_name: "",
      product_category: {
        product_category_id: "",
        product_category_name: "",
      },
      product_unit: {
        product_unit_id: "",
        product_unit_name: "",
      },
      product_rack: "",
      product_hjb: "",
      product_hjr: "",
      product_partner_selling_price: "",
      product_total_stock: 0,
    });
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // =====================
  // Request Method
  // =====================

  const getProductData = useCallback(async () => {
    try {
      setIsLoading(true);

      localStorage.setItem("searchValue", "");

      const response = await fetch(
        Constant.BASE_API_URL + "/produk?page=" + page,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const product = data.products;
      if (!product) {
        setListProductData([]);
      }
      setListProductData(product);
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setListProductData([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, navigate]);

  const createNewProduct = async (productData) => {
    try {
      setIsLoading(true);
      const response = await fetch(Constant.BASE_API_URL + "/produk", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          product_name: productData.product_name,
          product_category_id: productData.product_category.product_category_id,
          product_unit_id: productData.product_unit.product_unit_id,
          product_rack: productData.product_rack,
          product_hjb: productData.product_hjb,
          product_hjr: productData.product_hjr,
          product_partner_selling_price:
            productData.product_partner_selling_price,
          product_total_stock: productData.product_total_stock,
        }),
      });
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getProductData();
    }
  };

  const editProduct = async (productData) => {
    try {
      setIsLoading(true);
      const productId = productData.product_id;
      const response = await fetch(
        Constant.BASE_API_URL + "/produk/" + productId,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({
            product_name: productData.product_name,
            product_category_id:
              productData.product_category.product_category_id,
            product_unit_id: productData.product_unit.product_unit_id,
            product_rack: productData.product_rack,
            product_hjb: productData.product_hjb,
            product_hjr: productData.product_hjr,
            product_partner_selling_price:
              productData.product_partner_selling_price,
            product_total_stock: productData.product_total_stock,
          }),
        }
      );
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getProductData();
    }
  };

  const deleteProduct = async () => {
    try {
      setIsLoading(true);
      const productId = idProductToDelete;
      const response = await fetch(
        Constant.BASE_API_URL + "/produk/" + productId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdProductToDelete("");
      getProductData();
    }
  };

  const searchProduct = (searchText) => {
    setSearchProductKeyword(searchText);
    setPage(1);
  };

  const getProductSearchData = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = new URL(Constant.BASE_API_URL + "/produk/search");
      url.searchParams.append("keyword", searchProductKeyword);
      url.searchParams.append("page", page);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const product = data.products;
      if (!product) {
        setListProductData(null);
      } else {
        setListProductData(product);
      }
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setListProductData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, searchProductKeyword, navigate]);

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    if (searchProductKeyword !== "") {
      getProductSearchData();
    } else {
      getProductData();
    }
  }, [searchProductKeyword, getProductData, getProductSearchData]);

  // =====================
  // Render Method
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Produk</h1>
      <ProductHeader
        onAddProductClicked={addNewProduct}
        onSearchProductEdited={searchProduct}
      />
      <ProductTable
        page={page}
        product={listProductData}
        onEditProductClicked={onEditProduct}
        onDeleteProductClicked={onDeleteProduct}
        onInfoProductClicked={onInfoProductClicked}
      />
      <ModalAddProduct
        isShowModal={isOpenModalAddProduct}
        onCloseModal={onCloseModal}
        onSubmitData={onSubmitNewProduct}
        productObject={selectedProductData}
        key={Date.now()}
      />
      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeleteProduct}
        onCloseModal={onCloseModalDeleteProduct}
        onDeleteConfirmed={onDeleteProductConfirmed}
        idToDelete={idProductToDelete}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
      <ModalProductInfo
        isShowModal={isOpenModalProductInfo}
        onCloseModal={onCloseModalProductInfo}
        productData={productDataNote}
      />
    </div>
  );
};

export default ProductList;
